import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import SuspenseQuery from 'components/SuspenseQuery';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import { CapitalDepositDraftStatutesDocument } from './graphql/queries/CapitalDepositDraftStatutes.gql';
import getDraftStatutesDocument from './libs/getDraftStatutesDocument';
import machineConfig, { model, State } from './machine';
import DraftStatutesPrepare from './pages/DraftStatutesPrepare';
import DraftStatutesReview from './pages/DraftStatutesReview';
import DraftStatutesUpload from './pages/DraftStatutesUpload';

type CapitalDepositDraftStatutesProps = {
  companyProfileId: string;
  isMandatee?: boolean; // If true, the locales will be adapted for a mandatee
};

const CapitalDepositDraftStatutesComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const onPrev = useCallback(() => {
      send(model.events.PREV());
    }, [send]);

    const { data, isMandatee } = moduleData;
    const { company } = data.viewer;

    const draftStatutesDocument = getDraftStatutesDocument(company);
    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.PREPARE:
        return (
          <DraftStatutesPrepare
            isMandatee={isMandatee}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );

      case State.UPLOAD:
        return (
          <DraftStatutesUpload
            companyProfileId={company.companyProfileId}
            isMandatee={isMandatee}
            onPrev={onPrev}
            onUploadComplete={(documentFile) =>
              send(model.events.SUBMIT_DOCUMENT_FILE(documentFile))
            }
          />
        );

      case State.REVIEW:
      case State.DELETE:
        return (
          <DraftStatutesReview
            draftStatutesDocument={draftStatutesDocument}
            isDeleting={state.matches(State.DELETE)}
            isMandatee={isMandatee}
            onContinue={() => send(model.events.NEXT())}
            onDelete={(kybDocumentId) =>
              send(model.events.DELETE_DOCUMENT({ kybDocumentId }))
            }
            onPrev={onPrev}
          />
        );

      case State.INIT:
      case State.SAVE:
      case State.DONE:
      case State.ABORT:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

const CapitalDepositDraftStatutes =
  asBentoModule<CapitalDepositDraftStatutesProps>(
    ({ companyProfileId, isMandatee = false, onDone }) => {
      const query = useQuery(CapitalDepositDraftStatutesDocument, {
        variables: { companyProfileId },
      });

      return (
        <SuspenseQuery
          loader={<OBLoaderPage onPrev={() => null} />}
          query={query}
        >
          {(data) => (
            <CapitalDepositDraftStatutesComponent
              moduleData={{ data, isMandatee }}
              onDone={onDone}
            />
          )}
        </SuspenseQuery>
      );
    },
  );

export default CapitalDepositDraftStatutes;
